import type { ReactNode } from 'react';
import './DefinitionList.css';

export interface DefinitionItem {
  term: ReactNode;
  value: ReactNode;
  /** Optional clarifier rendered under the value — where "why is this greyed out?" goes. */
  hint?: ReactNode;
}

export interface DefinitionListProps {
  items: DefinitionItem[];
  /**
   * `stacked` puts the term above the value. Below ~40rem the component stacks
   * regardless, because a two-column term/value grid at phone width squeezes the
   * value to a few characters.
   */
  layout?: 'inline' | 'stacked';
}

/**
 * Term/value pairs (design-system catalogue, Tier 3) — the shape every detail
 * panel needs.
 *
 * Uses a real `<dl>`/`<dt>`/`<dd>`, so assistive tech announces the pairing
 * instead of reading two unrelated runs of text. Widths come from
 * `grid-template-columns: max-content 1fr`, not a chosen label width: a
 * hardcoded width is a guess about the longest term, and the first term that
 * exceeds it wraps and collides with its value — which is exactly the bug a
 * one-off version of this in `apps/settings` shipped with before this entry
 * existed.
 */
export function DefinitionList({ items, layout = 'inline' }: DefinitionListProps) {
  return (
    <dl className={`dpr-deflist dpr-deflist--${layout}`}>
      {items.map((item, i) => (
        // Index keys are safe here: the list is caller-ordered and never reordered
        // or filtered in place.
        <div className="dpr-deflist__row" key={i}>
          <dt className="dpr-deflist__term">{item.term}</dt>
          <dd className="dpr-deflist__value">
            {item.value}
            {item.hint ? <span className="dpr-deflist__hint">{item.hint}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
